import { useMemo } from 'react'
import { type CustomerModel } from 'pages/Customers/helpers/types'
import { EMBEDDED_CUSTOMER_STATUSES, PAGE_SIZE } from 'pages/EmbeddedCustomers/helpers/constants'
import { CustomerStatus, type CustomersFilters } from 'types/customers'
import { useCustomersQuery } from './useCustomersQuery'

type Totals = Record<string, number | undefined>
type Loaders = Record<string, boolean>
type Errors = Record<string, boolean>

export const useCustomerStatusCounts = (
  customerModel: CustomerModel,
  enableEpcDashboard: boolean
) => {
  const baseFilters: CustomersFilters = useMemo(
    () => ({
      models: [customerModel],
    }),
    [customerModel]
  )

  const verifiedParams = useMemo(
    () => ({
      filters: { ...baseFilters, statuses: [CustomerStatus.VERIFIED] },
      page: 0,
      size: PAGE_SIZE,
    }),
    [baseFilters]
  )

  const agreementsPendingParams = useMemo(
    () => ({
      filters: { ...baseFilters, statuses: [EMBEDDED_CUSTOMER_STATUSES.PENDING_AGREEMENTS] },
      page: 0,
      size: PAGE_SIZE,
    }),
    [baseFilters]
  )

  const pendingParams = useMemo(
    () => ({
      filters: { ...baseFilters, statuses: [EMBEDDED_CUSTOMER_STATUSES.PENDING] },
      page: 0,
      size: PAGE_SIZE,
    }),
    [baseFilters]
  )

  const infoRequiredParams = useMemo(
    () => ({
      filters: { ...baseFilters, statuses: [EMBEDDED_CUSTOMER_STATUSES.INFO_REQUIRED] },
      page: 0,
      size: PAGE_SIZE,
    }),
    [baseFilters]
  )

  const {
    data: verified,
    isFetching: loadingVerified,
    isError: errorVerified,
  } = useCustomersQuery(verifiedParams, enableEpcDashboard)

  const {
    data: agreementsPending,
    isFetching: loadingAgreementsPending,
    isError: errorAgreementsPending,
  } = useCustomersQuery(agreementsPendingParams, enableEpcDashboard)

  const {
    data: pending,
    isFetching: loadingPending,
    isError: errorPending,
  } = useCustomersQuery(pendingParams, enableEpcDashboard)

  const {
    data: infoRequired,
    isFetching: loadingInfoRequired,
    isError: errorInfoRequired,
  } = useCustomersQuery(infoRequiredParams, enableEpcDashboard)

  const totals: Totals = useMemo(
    () => ({
      VERIFIED: verified?.pagination?.total,
      PENDING_AGREEMENTS: agreementsPending?.pagination?.total,
      PENDING: pending?.pagination?.total,
      INFO_REQUIRED: infoRequired?.pagination?.total,
    }),
    [verified, agreementsPending, pending, infoRequired]
  )

  const loading: Loaders = useMemo(
    () => ({
      VERIFIED: loadingVerified,
      PENDING_AGREEMENTS: loadingAgreementsPending,
      PENDING: loadingPending,
      INFO_REQUIRED: loadingInfoRequired,
    }),
    [loadingVerified, loadingAgreementsPending, loadingPending, loadingInfoRequired]
  )

  const errors: Errors = useMemo(
    () => ({
      VERIFIED: !!errorVerified,
      PENDING_AGREEMENTS: !!errorAgreementsPending,
      PENDING: !!errorPending,
      INFO_REQUIRED: !!errorInfoRequired,
    }),
    [errorVerified, errorAgreementsPending, errorPending, errorInfoRequired]
  )

  return { totals, loading, errors }
}
